import { ORDER_METHODS } from "../../classes/constants";
import { useMap } from "@hydrosoft/map-api";
import { twMerge } from "tailwind-merge";
import { message, Slider } from "@hydrosoft/components";
import React, { forwardRef, useImperativeHandle } from "react";
import { useRef, useState } from "react";
import { PointGridClass, PointGridSettings, } from "../../classes/pointGridClass";
import { extend } from "@hydrosoft/utils";
import { FormattedMessage, useIntl } from "react-intl";
const defaultStyle = {
    color: [200, 0, 0],
    fillOpacity: 1,
    width: 15,
    height: 15,
    borderRadius: 15,
};
const defaultLabel = {
    text: "<%=values['number']%>",
    strokeWidth: 3,
    size: 10,
};
const defaultProps = {
    orderMethod: ORDER_METHODS.SNAKE,
    firstPointNumber: 1,
    active: true,
    rotation: {
        min: 0,
        max: 90,
        step: 1,
        units: "°",
    },
    spacing: {
        min: 10_000,
        max: 100_000,
        step: 10_000,
        units: "ha",
    },
    buffer: {
        min: 0,
        max: 40,
        step: 2,
        units: "m",
    },
    offset: {
        min: -50,
        max: 50,
        step: 1,
        units: "m",
    },
    defaults: {
        rotation: 0,
        spacing: 10_000,
        buffer: 10,
        offsetX: 0,
        offsetY: 0,
    },
    style: defaultStyle,
    label: defaultLabel,
    isActive: true,
};
const PointGrid = forwardRef((props, ref) => {
    props.rotation = extend(defaultProps.rotation, props.rotation);
    props.spacing = extend(defaultProps.spacing, props.spacing);
    props.buffer = extend(defaultProps.buffer, props.buffer);
    props.offset = extend(defaultProps.offset, props.offset);
    const defaults = extend(defaultProps.defaults, props.defaults);
    const graphicsLayerId = "_point_grid";
    const [state, setState] = useState({
        ...defaults,
        saveDisabled: true,
    });
    const stateRef = useRef();
    stateRef.current = state;
    const mapRef = useRef();
    const pointGridRef = useRef();
    const intl = useIntl();
    const onSaveCallbacks = useRef([]);
    const initState = (map) => {
        props.afterRender();
        mapRef.current = map;
        map.addGraphics(graphicsLayerId, map.getMaxZIndex() + 1);
        map.getGraphics(graphicsLayerId).setVisible(props.active);
        pointGridRef.current = new PointGridClass(map, props.mask, graphicsLayerId, new PointGridSettings({
            value: defaults.spacing,
            units: props.spacing.units,
        }, {
            value: defaults.rotation,
            units: props.rotation.units,
        }, {
            value: defaults.buffer,
            units: props.buffer.units,
        }, {
            value: defaults.offsetX,
            units: props.offset.units,
        }, {
            value: defaults.offsetY,
            units: props.offset.units,
        }), extend(defaultStyle, props.style), extend(defaultLabel, props.label));
        pointGridRef.current.render();
    };
    useImperativeHandle(ref, () => {
        return {
            enable() {
                mapRef.current.getGraphics(graphicsLayerId).setVisible(true);
            },
            disable() {
                mapRef.current.getGraphics(graphicsLayerId).setVisible(false);
            },
            onSave(callback) {
                onSaveCallbacks.current.push(callback);
            },
        };
    }, []);
    useMap(props.map, initState);
    const onRotation = (event, name, value) => {
        const rotation = parseInt(value);
        pointGridRef.current.getSettings().setRotation(rotation);
        setState({
            ...state,
            rotation,
            saveDisabled: true,
        });
    };
    const onSpacing = (event, name, value) => {
        const spacing = parseInt(value);
        pointGridRef.current.getSettings().setSpacing(spacing);
        setState({
            ...state,
            spacing,
            saveDisabled: true,
        });
    };
    const onBuffer = (event, name, value) => {
        const buffer = parseInt(value);
        pointGridRef.current.getSettings().setBuffer(buffer);
        setState({
            ...state,
            buffer,
            saveDisabled: true,
        });
    };
    const onOffsetX = (event, name, value) => {
        const offsetX = parseInt(value);
        pointGridRef.current.getSettings().setOffsetX(offsetX);
        setState({
            ...state,
            offsetX,
            saveDisabled: true,
        });
    };
    const onOffsetY = (event, name, value) => {
        const offsetY = parseInt(value);
        pointGridRef.current.getSettings().setOffsetY(offsetY);
        setState({
            ...state,
            offsetY,
            saveDisabled: true,
        });
    };
    const onOrderClick = () => {
        pointGridRef.current.order(props.firstPointNumber, props.orderMethod);
        setState({
            ...state,
            saveDisabled: false,
        });
    };
    const onSaveClick = () => {
        onSaveCallbacks.current.forEach((callback) => {
            callback.call(null, pointGridRef.current.export(), pointGridRef.current.getSettings());
        });
    };
    if (mapRef.current === undefined) {
        return null;
    }
    const className = twMerge("flex flex-col space-y-2 overflow-auto", props.className);
    return (React.createElement("div", { className: className },
        React.createElement("div", null,
            React.createElement("label", null, intl.formatMessage({ id: message("pointCount") }, {
                count: pointGridRef.current.getPointCount(),
            }))),
        React.createElement("div", null,
            React.createElement("label", null, intl.formatMessage({ id: message("rotation") }, {
                rotation: pointGridRef.current.getSettings().getRotation(true),
            })),
            React.createElement(Slider, { name: "rotation", min: props.rotation.min, max: props.rotation.max, step: props.rotation.step, value: stateRef.current.rotation, onInput: onRotation })),
        React.createElement("div", null,
            React.createElement("label", { dangerouslySetInnerHTML: {
                    __html: intl.formatMessage({ id: message("spacing") }, {
                        spacing: pointGridRef.current.getSettings().getSpacing(true),
                    }),
                } }),
            React.createElement(Slider, { name: "spacing", min: props.spacing.min, max: props.spacing.max, step: props.spacing.step, value: stateRef.current.spacing, onInput: onSpacing })),
        React.createElement("div", null,
            React.createElement("label", null, intl.formatMessage({ id: message("buffer") }, {
                buffer: pointGridRef.current.getSettings().getBuffer(true),
            })),
            React.createElement(Slider, { name: "buffer", min: props.buffer.min, max: props.buffer.max, step: props.buffer.step, value: stateRef.current.buffer, onInput: onBuffer })),
        React.createElement("div", null,
            React.createElement("label", null, intl.formatMessage({ id: message("offsetX") }, {
                offset: pointGridRef.current.getSettings().getOffsetX(true),
            })),
            React.createElement(Slider, { name: "offsetX", min: props.offset.min, max: props.offset.max, step: props.offset.step, value: stateRef.current.offsetX, onInput: onOffsetX })),
        React.createElement("div", null,
            React.createElement("label", null, intl.formatMessage({ id: message("offsetY") }, {
                offset: pointGridRef.current.getSettings().getOffsetY(true),
            })),
            React.createElement(Slider, { name: "offsetY", min: props.offset.min, max: props.offset.max, step: props.offset.step, value: stateRef.current.offsetY, onInput: onOffsetY })),
        React.createElement("div", null,
            React.createElement("button", { className: "btn btn-outline-secondary w-full", onClick: onOrderClick },
                React.createElement(FormattedMessage, { id: message("order") }))),
        React.createElement("div", null,
            React.createElement("button", { className: "btn btn-outline-secondary w-full", onClick: onSaveClick, disabled: stateRef.current.saveDisabled },
                React.createElement(FormattedMessage, { id: message("save") })))));
});
PointGrid.defaultProps = defaultProps;
export default PointGrid;
//# sourceMappingURL=PointGrid.js.map